import { Link } from "react-router-dom";

interface ComicCardProps {
  id: number;
  title: string;
  author: string;
  coverImage: string;
  rating: number;
  category?: string;
  status?: string;
  chapters?: number;
  views?: string;
  variant?: "default" | "compact" | "featured";
}

export default function ComicCard({
  id,
  title,
  author,
  coverImage,
  rating,
  category,
  status,
  chapters,
  views,
  variant = "default",
}: ComicCardProps) {
  if (variant === "compact") {
    return (
      <Link
        to={`/article/${id}`}
        className="group flex-shrink-0 w-[160px] md:w-[180px] cursor-pointer"
        data-product-shop=""
      >
        <div className="relative rounded-lg overflow-hidden aspect-[3/4] mb-2.5 bg-background-200">
          <img
            src={coverImage}
            alt={title}
            className="w-full h-full object-cover object-top transition-transform duration-400 group-hover:scale-105"
            loading="lazy"
          />
          {status && (
            <span className="absolute top-2 left-2 px-2 py-0.5 rounded-md text-[10px] font-medium bg-primary-500 text-background-50">
              {status}
            </span>
          )}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3 pt-8">
            <div className="flex items-center gap-1">
              <div className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-star-fill text-primary-400 text-xs"></i>
              </div>
              <span className="text-background-50 text-xs font-medium">{rating}</span>
            </div>
          </div>
        </div>
        <h3 className="text-sm font-medium text-foreground-900 truncate group-hover:text-primary-500 transition-colors">
          {title}
        </h3>
        <p className="text-xs text-foreground-500 mt-0.5">{author}</p>
      </Link>
    );
  }

  if (variant === "featured") {
    return (
      <Link
        to={`/article/${id}`}
        className="group flex-shrink-0 w-[280px] md:w-[320px] cursor-pointer"
        data-product-shop=""
      >
        <div className="relative rounded-xl overflow-hidden aspect-[16/10] mb-3 bg-background-200">
          <img
            src={coverImage}
            alt={title}
            className="w-full h-full object-cover object-top transition-transform duration-400 group-hover:scale-105"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-4">
            <div className="flex items-center gap-2 mb-1.5">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-star-fill text-primary-400 text-sm"></i>
              </div>
              <span className="text-background-50 font-semibold">{rating}</span>
              {category && (
                <span className="px-2 py-0.5 rounded-md text-[10px] bg-background-50/20 text-background-50">
                  {category}
                </span>
              )}
            </div>
            <h3 className="text-base font-semibold text-background-50 group-hover:text-primary-300 transition-colors">
              {title}
            </h3>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${id}`}
      className="group cursor-pointer"
      data-product-shop=""
    >
      <div className="relative rounded-xl overflow-hidden aspect-[3/4] mb-3 bg-background-200">
        <img
          src={coverImage}
          alt={title}
          className="w-full h-full object-cover object-top transition-transform duration-400 group-hover:scale-105"
          loading="lazy"
        />
        <div className="absolute top-3 right-3">
          <span className="px-2.5 py-1 rounded-lg text-xs font-semibold bg-primary-500 text-background-50">
            {rating}
          </span>
        </div>
        {status && (
          <span className="absolute top-3 left-3 px-2 py-0.5 rounded-md text-[10px] font-medium bg-background-50/90 text-foreground-700">
            {status}
          </span>
        )}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent p-4 pt-12">
          <h3 className="text-sm font-semibold text-background-50 group-hover:text-primary-300 transition-colors leading-tight">
            {title}
          </h3>
          <p className="text-xs text-background-50/70 mt-1">{author}</p>
          {chapters && (
            <p className="text-[10px] text-background-50/60 mt-0.5">共{chapters}话</p>
          )}
        </div>
      </div>
    </Link>
  );
}